import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { TRACKING_ACTIONS, trackEvent } from '@medflex/utils/Tracking';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { IconClose } from '@medflex/components/stories/Icons';
import useNotifications from '../hooks/useNotifications';

const InitialDialog = ({ onAccept, onClose }) => {
  const { t } = useTranslation('messenger');

  const handleClick = e => {
    e.preventDefault();
    onAccept();
  };

  const handleClose = e => {
    e.preventDefault();
    onClose();
  };

  return (
    <div
      data-component="NotificationsModule"
      className="w-100 flex items-center justify-center bg-blue-900 py-4 text-white"
    >
      <span>
        {t('NotificationsModule.request.text')}
        &nbsp;
      </span>
      <button type="button" className="underline" onClick={handleClick}>
        {t('NotificationsModule.request.accept')}
      </button>
      <button
        type="button"
        className="absolute right-0 mr-4 flex items-center"
        onClick={handleClose}
      >
        <IconClose size="4" />
      </button>
    </div>
  );
};

InitialDialog.propTypes = {
  onAccept: PropTypes.func,
  onClose: PropTypes.func,
};

const Dialog = ({ onAccept, onSnooze, onDeny }) => {
  const { t } = useTranslation('messenger');

  const bypassEvent =
    (fn = () => {}) =>
    e => {
      e.preventDefault();
      fn();
    };

  return (
    <div className="NotificationsModule w-100 bg-blue-900 py-4 text-center text-white">
      <p>{t('NotificationsModule.reminder.text')}</p>
      <div>
        <button type="button" className="underline" onClick={bypassEvent(onAccept)}>
          {t('NotificationsModule.reminder.accept')}
        </button>
        <span className="mx-2">&bull;</span>
        <button type="button" className="underline" onClick={bypassEvent(onSnooze)}>
          {t('NotificationsModule.reminder.snooze')}
        </button>
        <span className="mx-2">&bull;</span>
        <button type="button" className="underline" onClick={bypassEvent(onDeny)}>
          {t('NotificationsModule.reminder.deny')}
        </button>
      </div>
    </div>
  );
};

Dialog.propTypes = {
  onAccept: PropTypes.func,
  onSnooze: PropTypes.func,
  onDeny: PropTypes.func,
};

const NotificationsModule = () => {
  const { isSupported, isBlocked, isInitialRequest, isGranted, accept, deny, snooze, notify } =
    useNotifications();
  const { pushNotifications } = useSelector(state => state.notifications);
  const history = useHistory();

  const handleSnooze = () => {
    trackEvent({
      zone: 'Notification_Dialog',
      action: TRACKING_ACTIONS.CLICK,
      actionText: 'snoozed',
    });
    snooze();
  };

  const handleClose = () => {
    trackEvent({
      zone: 'Notification_Dialog',
      action: TRACKING_ACTIONS.CLICK,
      actionText: 'closed',
    });
    snooze(0);
  };

  const handleDeny = () => {
    trackEvent({
      zone: 'Notification_Dialog',
      action: TRACKING_ACTIONS.CLICK,
      actionText: 'denied',
    });
    deny();
  };

  const handleAccept = () => {
    trackEvent({
      zone: 'Notification_Dialog',
      action: TRACKING_ACTIONS.CLICK,
      actionText: 'granted',
    });
    accept();
  };

  useEffect(() => {
    if (!pushNotifications.length) return;
    const newNotification = pushNotifications.pop();
    const { title, body, path, icon } = newNotification;
    const notification = notify(title, {
      requireInteraction: true,
      body,
      ...(icon && { icon }),
    });

    if (!notification) return;
    notification.addEventListener('show', () => {
      trackEvent({
        zone: 'Notification',
        action: 'show',
        actionText: 'shown',
      });
    });

    notification.addEventListener(
      'click',
      () => {
        notification.close();
        window.focus();
        history.push(path);
        trackEvent({
          zone: 'Notification',
          action: TRACKING_ACTIONS.CLICK,
          actionText: 'clicked',
        });

        if (window.electron && window.electron.globals && window.electron.globals.notifications) {
          window.electron.globals.notifications.onClick();
        }
      },
      false,
    );

    notification.addEventListener('close', () => {
      trackEvent({
        zone: 'Notification',
        action: 'closed',
        actionText: 'closed',
      });
    });
  }, [pushNotifications]);

  if (isSupported) {
    if (isInitialRequest) {
      return <InitialDialog onAccept={handleAccept} onClose={handleClose} />;
    }

    if (!isGranted && !isBlocked) {
      return <Dialog onAccept={handleAccept} onDeny={handleDeny} onSnooze={handleSnooze} />;
    }
  }

  return null;
};

export default NotificationsModule;
